import { useId } from 'react';

/**
 * Minimal inline-SVG trend line. Values are plotted left-to-right on a 0..max scale
 * (or 0..100 when `percent`). Null values leave a gap.
 */
export function Sparkline({
  values,
  width = 220,
  height = 56,
  percent,
  label,
}: {
  values: (number | null)[];
  width?: number;
  height?: number;
  percent?: boolean;
  label: string;
}) {
  const gid = useId();
  const pad = 4;
  const nums = values.filter((v): v is number => v !== null);
  const max = percent ? 100 : Math.max(1, ...nums);
  const step = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) =>
    v === null ? null : ([pad + i * step, height - pad - (v / max) * (height - pad * 2)] as const),
  );

  const segments: string[] = [];
  let current = '';
  for (const p of pts) {
    if (!p) {
      if (current) segments.push(current);
      current = '';
      continue;
    }
    current += `${current ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`;
  }
  if (current) segments.push(current);

  const valid = pts.filter((p): p is readonly [number, number] => p !== null);
  const first = valid[0];
  const last = valid[valid.length - 1];
  const area =
    first && last && valid.length > 1
      ? `M${first[0]},${height - pad}` + valid.map((p) => `L${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('') + `L${last[0]},${height - pad}Z`
      : '';

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--color-primary)' }} stopOpacity="0.24" />
          <stop offset="1" style={{ stopColor: 'var(--color-primary)' }} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${gid}-stroke`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: 'var(--color-primary)' }} />
          <stop offset="1" style={{ stopColor: 'var(--color-primary)' }} />
        </linearGradient>
      </defs>
      {area && <path d={area} fill={`url(#${gid}-fill)`} />}
      {segments.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={`url(#${gid}-stroke)`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      ))}
      {last && <circle cx={last[0]} cy={last[1]} r="3.5" style={{ fill: 'var(--color-primary)', stroke: 'var(--color-surface)' }} strokeWidth="1.5" />}
    </svg>
  );
}
